import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, FileText, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { JustificativaFilaDialog } from '@/components/kanban/triagem/JustificativaFilaDialog';
import { TriagemEsim } from '@/components/kanban/fluxo/TriagemEsim';
import { TriagemPrimeiraFatura } from '@/components/kanban/fluxo/TriagemPrimeiraFatura';
import { useUserProfile } from '@/hooks/useUserProfile';
import { TravaContaVencidaLista, type ContaDaTrava } from './TravaContaVencidaLista';
import { TravaContaConferirLista, type ContaConferir } from './TravaContaConferirLista';

/* TRAVAS DA QUALIDADE (Douglas 22/10/2026, maquete aprovada): a Jéssica trava
   quando o cliente do eSIM espera resposta dela há 2 h úteis, ou quando ela
   ajudou e 24 h úteis depois a pergunta não saiu de novo; a Luenhy, às 9h e às
   14h, com as contas vencidas que não estão no CRM. O Inbox fica aberto (lá a
   trava vira faixa). Quem decide é fn_qualidade_trava_travado; o painel só abre
   a triagem e as conversas que já existem. Desde 28/10 há também a trava das
   10h, das contas no CRM que faltam conferir no SOLAR (conta_conferir). */

type Fila = 'esim_espera' | 'esim_repergunta' | 'conta_vencida' | 'conta_conferir';
type PedidoTrava = {
  pedido_id: string; numero_pedido: number | null; cliente: string | null; conversa_id: string | null; batido: boolean;
  conta?: ContaDaTrava | ContaConferir | null; justificativa?: string | null;
};
type Trava = { aplica: boolean; travado: boolean; fila?: Fila | null; hora?: string | null; pedidos_trava?: PedidoTrava[]; travas_hoje?: number };

const COLUNA_DA_FILA: Partial<Record<Fila, string>> = {
  conta_vencida: 'CONTA VENCIDA SEM CONTA',
  conta_conferir: 'CONTA PARA CONFERIR',
};
const DE_CONTA: Fila[] = ['conta_vencida', 'conta_conferir'];

const FILAS: Record<Fila, { eyebrow: string; titulo: string; texto: string; triagem: string; conversa: boolean; justifica: boolean; regua: string }> = {
  esim_espera: {
    eyebrow: 'Analista eSIM',
    titulo: 'Clientes esperando a sua ajuda',
    texto: 'Estes clientes responderam à pergunta do eSIM, pedindo ajuda ou escrevendo, e há mais de 2 h de expediente estão sem resposta sua. Responda pela conversa, com mensagem ou áudio.',
    triagem: 'Triagem Validação eSIM',
    conversa: true,
    justifica: false,
    regua: 'Cliente esperando: 2 h de expediente · Ajudou sem perguntar de novo: 1 dia útil · trava quando um cliente passa do prazo',
  },
  esim_repergunta: {
    eyebrow: 'Analista eSIM',
    titulo: 'Clientes ajudados sem a pergunta de volta',
    texto: 'Você falou com estes clientes há mais de 1 dia útil e a pergunta não saiu de novo. Aperte Resolvido — pedir confirmação, Reenviar pergunta, Ainda não tem o chip ou Agendar retorno.',
    triagem: 'Triagem Validação eSIM',
    conversa: true,
    justifica: false,
    regua: 'Cliente esperando: 2 h de expediente · Ajudou sem perguntar de novo: 1 dia útil · trava quando um cliente passa do prazo',
  },
  conta_vencida: {
    eyebrow: 'Analista de faturas',
    titulo: 'Contas vencidas que não temos',
    texto: 'A conta destes clientes venceu e não está no CRM. Copie o CNPJ e confira no SOLAR: pagou, marque Pagou; senão, anexe a conta. Não deu agora, justifique.',
    triagem: 'Triagem Faturas · 180 dias',
    conversa: false,
    justifica: true,
    regua: 'Conta vencida sem conta: 1 dia útil por conta, contado do vencimento ou da sua última ação nela',
  },
  conta_conferir: {
    eyebrow: 'Analista de faturas',
    titulo: 'Contas vencidas para conferir',
    texto: 'A conta destes clientes venceu e já está no CRM. Copie o CNPJ, confira no SOLAR e marque Pagou ou Não pagou. Terminou, destrava.',
    triagem: 'Triagem Faturas · 180 dias',
    conversa: false,
    justifica: true,
    regua: 'Às 10h, ou na primeira entrada depois das 10h · entram as contas suas que venceram, estão no CRM e não têm Pago nem Não pago',
  },
};

export function QualidadeTrava() {
  const { data: perfil } = useUserProfile();
  const aplica = perfil?.funcao === 'ANALISTA_QUALIDADE';
  const [aberta, setAberta] = useState(false);
  const [justificando, setJustificando] = useState<PedidoTrava | null>(null);
  const [salvando, setSalvando] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['qualidade-trava', perfil?.id],
    enabled: aplica,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Trava> => {
      const { data, error } = await rpcTriagem('fn_qualidade_trava_travado', {});
      if (error) throw new Error(error.message || 'Não deu para conferir a trava da Qualidade.');
      return data as Trava;
    },
  });
  if (!aplica || !q.data?.travado || !q.data.fila) return null;
  const t = q.data;
  const filaT: Fila = q.data.fila;
  const fila = FILAS[filaT];
  const pedidos = t.pedidos_trava ?? [];
  const faltam = pedidos.filter((p) => !p.batido).length;
  const fechar = () => { setAberta(false); q.refetch(); };
  const abrirConversa = (id: string | null) => { if (id) navigate(`/whatsapp-inbox?conversation=${id}`); };
  const justificar = async (texto: string, codigos: { motivo: string | null; acao: string | null }) => {
    if (!justificando) return;
    setSalvando(true);
    try {
      const { error } = await rpcTriagem('fn_qualidade_trava_justificar', {
        p_pedido: justificando.pedido_id, p_motivo: codigos.motivo, p_acao: codigos.acao, p_texto: texto, p_fila: filaT,
      });
      if (error) { toast.error(error.message || 'Não foi possível justificar.'); throw new Error(error.message); }
      toast.success('Justificativa registrada.');
      q.refetch();
    } finally { setSalvando(false); }
  };

  const modal = <>
    {fila.triagem === FILAS.conta_vencida.triagem
      ? <TriagemPrimeiraFatura isOpen={aberta} onClose={fechar} />
      : <TriagemEsim isOpen={aberta} onClose={fechar} />}
    {justificando && COLUNA_DA_FILA[filaT] && (
      <JustificativaFilaDialog open={!!justificando} onOpenChange={(o) => { if (!o) setJustificando(null); }}
        coluna={COLUNA_DA_FILA[filaT]!} numeroPedido={String(justificando.numero_pedido ?? '-')} razaoSocial={justificando.cliente || '-'}
        isLoading={salvando} onConfirm={justificar} />
    )}
  </>;

  if (pathname.startsWith('/whatsapp-inbox')) {
    const proximo = pedidos.find((p) => !p.batido);
    return (
      <div role="status" className="fixed left-1/2 top-3 z-[9000] flex w-[min(94vw,720px)] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-4 py-2 text-white shadow-lg">
        <Lock className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 text-[12.5px]"><span className="font-bold">Trava das {t.hora}:</span> {fila.titulo.toLowerCase()} · faltam <span className="font-bold tabular-nums">{faltam}</span> de {pedidos.length}</p>
        {fila.conversa && proximo?.conversa_id && (
          <button type="button" onClick={() => abrirConversa(proximo.conversa_id)}
            className="rounded-md border border-white/40 px-2 py-1 text-[11.5px] font-bold hover:bg-white/10">
            Próximo: {proximo.numero_pedido ?? '-'}
          </button>
        )}
        <button type="button" onClick={() => setAberta(true)} className="rounded-md border border-white/40 px-2 py-1 text-[11.5px] font-bold hover:bg-white/10">{fila.triagem}</button>
        {modal}
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="trava-qualidade-titulo"
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-background/95 p-4 backdrop-blur-[2px]">
      <div className={`w-full ${DE_CONTA.includes(filaT) ? 'max-w-[680px]' : 'max-w-[560px]'} overflow-hidden rounded-xl border border-border-warm bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]`}>
        <div className="bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-white/60">{fila.eyebrow} · trava das {t.hora ?? '-'}</p>
          <h2 id="trava-qualidade-titulo" className="mt-1 font-display text-[22px] font-bold">{fila.titulo}</h2>
          <p className="mt-1 text-[12.5px] text-white/85">{fila.texto}</p>
          <p className="mt-3 font-display text-[34px] font-bold leading-none tabular-nums">Faltam {faltam} de {pedidos.length}</p>
        </div>
        <div className="border-b border-border px-6 py-3">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pedidos da trava</p>
            <p className="text-[11px] text-muted-foreground">Travas hoje: <span className="font-bold tabular-nums text-primary">{t.travas_hoje ?? 0}</span></p>
          </div>
          <div className={`${DE_CONTA.includes(filaT) ? 'max-h-[320px]' : 'max-h-[220px]'} overflow-y-auto`}>
            {t.fila === 'conta_vencida' ? (
              <TravaContaVencidaLista pedidos={pedidos as Parameters<typeof TravaContaVencidaLista>[0]['pedidos']} aoMudar={() => void q.refetch()}
                aoJustificar={(p) => setJustificando(pedidos.find((x) => x.pedido_id === p.pedido_id) ?? null)} />
            ) : t.fila === 'conta_conferir' ? (
              <TravaContaConferirLista pedidos={pedidos as Parameters<typeof TravaContaConferirLista>[0]['pedidos']} aoMudar={() => void q.refetch()}
                aoJustificar={(p) => setJustificando(pedidos.find((x) => x.pedido_id === p.pedido_id) ?? null)} />
            ) : pedidos.map((p) => (
              <div key={p.pedido_id} className="flex h-8 items-center justify-between gap-3 border-t border-border text-[12.5px]">
                <span className="min-w-0 truncate">
                  <span className="font-semibold tabular-nums text-foreground">{p.numero_pedido ?? '-'}</span>
                  <span className="text-muted-foreground"> · {p.cliente || '-'}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {fila.conversa && !p.batido && p.conversa_id && (
                    <button type="button" onClick={() => abrirConversa(p.conversa_id)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary">
                      <MessageCircle className="h-3 w-3" /> Conversa
                    </button>
                  )}
                  {fila.justifica && !p.batido && (
                    <button type="button" onClick={() => setJustificando(p)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary">
                      <FileText className="h-3 w-3" /> Justificar
                    </button>
                  )}
                  {p.batido
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success"><Check className="h-3 w-3" /> batido</span>
                    : <span className="text-[11px] text-primary">falta</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3 px-6 py-4">
          <p className="text-[12px] text-muted-foreground">{fila.regua}</p>
          <button type="button" onClick={() => setAberta(true)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] text-[13px] font-bold text-white transition hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]">
            {fila.triagem}
          </button>
          <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Livre</span>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground">WhatsApp Inbox</span>
            <span className="shrink-0 pl-2 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Travados</span>
            {['Kanban', 'Tarefas', 'Outras triagens'].map((n) => (
              <span key={n} className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground">
                <Lock className="h-3 w-3" /> {n}
              </span>
            ))}
          </div>
        </div>
      </div>
      {modal}
    </div>
  );
}
